import { Button, Grid } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import FilterTable from "../../components/table/FilterTable";
import { useDeleteDraftMutation, useDownloadDraftMutation } from "../../services/draftsService";
import TableService from "../../services/tableService";
import { DocxDraftAddDialog } from "./DocxDrafts/DocxDraftAddDialog";
import { DocxDraftEditDialog } from "./DocxDrafts/DocxDraftEditDialog";
import { DocxDraftType } from "./DocxDrafts/DocxDraftType";
import {
    OFFER_DRAFTS_TABLE_NAME,
    useDocxDraftsTableFieldsConfig,
} from "./DocxDrafts/DocxDraftsTableFieldsConfig";

export function OffersTab({
    offersDataLocal,
    setOffersDataLocal,
    offersRecordsCount,
    resetPageNumber,
    selectedRowId,
    setSelectedRowId,
    hiddenColumnsForTables,
    columnsOrdersForTables,
    checkedAll,
    setCheckedAll,
    dataPage,
    handleChangeDataPage,
    dataPageSize,
    handleChangeDataPageSize,
    searchParams,
}) {
    const { t } = useTranslation();
    const OFFERS_TABLE_FIELDS_CONFIG = useDocxDraftsTableFieldsConfig();

    const [addOfferDialogOpen, setAddOfferDialogOpen] = useState(false);
    const [editOfferDialogOpen, setEditOfferDialogOpen] = useState(false);
    const [selectedOfferForEdit, setSelectedOfferForEdit] = useState(null);

    const downloadDraftMutation = useDownloadDraftMutation();
    const deleteDraftMutation = useDeleteDraftMutation({
        onSuccess: () => {
            toast.success(t("drafts.deleteSuccess"));
        },
        onError: () => {
            toast.error(t("drafts.deleteError"));
        },
    });

    const onClickOffersButton = useCallback(
        (e, id, action) => {
            e.stopPropagation();

            if (action === "edit") {
                const offer = offersDataLocal.find((o) => o.id === id);
                if (offer) {
                    setSelectedOfferForEdit(offer);
                    setEditOfferDialogOpen(true);
                }
            }
            if (action === "download") {
                const offer = offersDataLocal.find((o) => o.id === id);
                if (offer) {
                    downloadDraftMutation.mutate(
                        {
                            draftId: offer.id,
                        },
                        {
                            onSuccess: (data) => {
                                const url = window.URL.createObjectURL(data);
                                const link = document.createElement("a");
                                link.href = url;
                                link.setAttribute("download", offer.file_name);
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                            },
                        },
                    );
                }
            }
            if (action === "delete") {
                deleteDraftMutation.mutate({ draftId: id });
            }
        },
        [offersDataLocal, downloadDraftMutation, deleteDraftMutation],
    );

    const offersDataExportHeaders = useMemo(() => {
        return TableService.getTableExportHeaders(
            OFFERS_TABLE_FIELDS_CONFIG,
            Array.isArray(hiddenColumnsForTables?.[OFFER_DRAFTS_TABLE_NAME])
                ? hiddenColumnsForTables[OFFER_DRAFTS_TABLE_NAME]
                : [],
            Array.isArray(columnsOrdersForTables?.[OFFER_DRAFTS_TABLE_NAME])
                ? columnsOrdersForTables[OFFER_DRAFTS_TABLE_NAME]
                : [],
        );
    }, [OFFERS_TABLE_FIELDS_CONFIG, hiddenColumnsForTables, columnsOrdersForTables]);

    const offersData = useMemo(
        () =>
            TableService.getPreparedDataForCollapsibleTable(
                OFFERS_TABLE_FIELDS_CONFIG,
                offersDataLocal,
                Array.isArray(hiddenColumnsForTables?.[OFFER_DRAFTS_TABLE_NAME])
                    ? hiddenColumnsForTables[OFFER_DRAFTS_TABLE_NAME]
                    : [],
                Array.isArray(columnsOrdersForTables?.[OFFER_DRAFTS_TABLE_NAME])
                    ? columnsOrdersForTables[OFFER_DRAFTS_TABLE_NAME]
                    : [],
                onClickOffersButton,
            ),
        [
            OFFERS_TABLE_FIELDS_CONFIG,
            offersDataLocal,
            hiddenColumnsForTables,
            columnsOrdersForTables,
            onClickOffersButton,
        ],
    );

    return (
        <>
            <Grid container spacing={10} justifyContent="left" sx={{ mb: 2 }}>
                <Grid item>
                    <Button variant="contained" onClick={() => setAddOfferDialogOpen(true)}>
                        {t("drafts.offerTemplates.addOffer")}
                    </Button>
                </Grid>
            </Grid>
            <FilterTable
                //@ts-ignore
                data={offersData}
                countRecords={offersRecordsCount}
                resetPageNumber={resetPageNumber}
                selectedItemId={selectedRowId}
                style={{ maxHeight: "60vh" }}
                headersConfig={TableService.orderColumns(
                    TableService.hideHiddenColumns(
                        OFFERS_TABLE_FIELDS_CONFIG,
                        Array.isArray(hiddenColumnsForTables?.[OFFER_DRAFTS_TABLE_NAME])
                            ? hiddenColumnsForTables[OFFER_DRAFTS_TABLE_NAME]
                            : [],
                    ),
                    Array.isArray(columnsOrdersForTables?.[OFFER_DRAFTS_TABLE_NAME])
                        ? columnsOrdersForTables[OFFER_DRAFTS_TABLE_NAME]
                        : [],
                )}
                showCleanFilterIcon={false}
                showContextMenu={false}
                showExportToFileButton={false}
                exportToFileUrl={"offers/export/"}
                exportToFileSearchParams={searchParams}
                exportToFileHeaders={offersDataExportHeaders}
                exportToFileFileName={`${t("drafts.offerTemplates.exportFileName")}.xlsx`}
                showDetailsIcon={false}
                showCheckbox={false}
                checkedAll={checkedAll}
                page={dataPage}
                onPageChange={handleChangeDataPage}
                rowsPerPage={dataPageSize}
                onRowsPerPageChange={handleChangeDataPageSize}
                setDataLocal={setOffersDataLocal}
                setCheckedAll={setCheckedAll}
                setSelectedRowId={setSelectedRowId}
                showCounterRecords={false}
                hideFiltersIconCol={true}
            />
            <DocxDraftAddDialog
                title={t("drafts.offerTemplates.addTemplate")}
                open={addOfferDialogOpen}
                onClose={() => setAddOfferDialogOpen(false)}
                mode={DocxDraftType.OFFER}
            />
            <DocxDraftEditDialog
                title={t("drafts.offerTemplates.editTemplate")}
                open={editOfferDialogOpen}
                onClose={() => {
                    setEditOfferDialogOpen(false);
                    setSelectedOfferForEdit(null);
                }}
                draft={selectedOfferForEdit}
                onSuccessUpdate={() => {}}
            />
        </>
    );
}
